import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Modal } from "react-bootstrap";
import { FiGift, FiRotateCcw, FiInbox, FiTrendingUp, FiClock, FiAward } from "react-icons/fi";
import Notification, { showToast } from "../../common/notification/Notification";
import Spinner from "../../common/spinner/Spinner";
import { redeemRewardPoints } from "../../../api/customer/customerFeatureApis";
import usePersistentState from "../../../hooks/usePersistentState";
import useCustomerRewards, { formatRewardTitle } from "./useCustomerRewards";
import "../../../modules/property/PropertiesPage.css";
import "../../../modules/property/components/PropertyPageHeader.css";
import "../../../modules/property/components/PropertyFilterToolbar.css";
import "./CustomerRewardsHub.css";

// Same redeem rules and shortcuts as the mobile Rewards screen
const MIN_POINTS_TO_REDEEM = 10;
const QUICK_REDEEM_OPTIONS = [10, 50, 100, 200];

const REDEEM_STATUS_OPTIONS = [
  { value: "PENDING", label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
];

// Mobile: APPROVED = green, REJECTED = red, anything else = pending (amber)
const normaliseStatus = (status) => {
  const s = String(status || "").toUpperCase();
  return s === "APPROVED" || s === "REJECTED" ? s : "PENDING";
};

const STATUS_BADGE = {
  APPROVED: { className: "bg-success", label: "Approved" },
  REJECTED: { className: "bg-danger", label: "Rejected" },
  PENDING: { className: "bg-warning text-dark", label: "Pending" },
};

const formatDate = (value, withTime) =>
  value
    ? new Date(value).toLocaleString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
        ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
      })
    : "—";

const byNewest = (a, b) => new Date(b.created_on || 0) - new Date(a.created_on || 0);

const inRange = (value, from, to) => {
  if (!from && !to) return true;
  if (!value) return false;
  const time = new Date(value).getTime();
  if (from && time < new Date(`${from}T00:00:00`).getTime()) return false;
  if (to && time > new Date(`${to}T23:59:59`).getTime()) return false;
  return true;
};

const CustomerRewardsHub = () => {
  const authState = useSelector((state) => state.auth);
  const loggedInUser = authState?.user ?? authState;
  const token =
    loggedInUser?.refreshToken ||
    authState?.refreshToken ||
    loggedInUser?.token ||
    authState?.token;

  const { points, logs, actions, redemptions, totals, loading, error, refetch } = useCustomerRewards(token);

  const [activeTab, setActiveTab] = usePersistentState("list:rewards:tab", "history"); // 'history' | 'redeem' | 'earn'
  const [statusFilter, setStatusFilter] = usePersistentState("list:rewards:status", "");
  const [dateFrom, setDateFrom] = usePersistentState("list:rewards:from", "");
  const [dateTo, setDateTo] = usePersistentState("list:rewards:to", "");

  const [isRedeemOpen, setIsRedeemOpen] = useState(false);
  const [redeemAmount, setRedeemAmount] = useState("");
  const [redeemError, setRedeemError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const hasFilters = Boolean(statusFilter || dateFrom || dateTo);

  const visibleLogs = useMemo(
    () => [...logs].sort(byNewest).filter((l) => inRange(l.created_on, dateFrom, dateTo)),
    [logs, dateFrom, dateTo]
  );
  const visibleRedemptions = useMemo(
    () =>
      [...redemptions]
        .sort(byNewest)
        .filter(
          (r) =>
            (!statusFilter || normaliseStatus(r.status) === statusFilter) &&
            inRange(r.created_on, dateFrom, dateTo)
        ),
    [redemptions, statusFilter, dateFrom, dateTo]
  );

  const resetFilters = () => {
    setStatusFilter("");
    setDateFrom("");
    setDateTo("");
  };

  const openRedeem = () => {
    setRedeemAmount("");
    setRedeemError(null);
    setIsRedeemOpen(true);
  };

  const handleRedeem = async (e) => {
    e.preventDefault();
    const amount = Number(redeemAmount);
    if (!redeemAmount || !Number.isInteger(amount) || amount <= 0) {
      setRedeemError("Please enter a valid number of points.");
      return;
    }
    if (amount > points) {
      setRedeemError(`You cannot redeem more than your available balance (${points} points).`);
      return;
    }
    try {
      setSubmitting(true);
      setRedeemError(null);
      const res = await redeemRewardPoints(token, amount);
      if (res?.statusType === "SUCCESS") {
        showToast(res.text || "Redeem request submitted successfully", { type: "success" });
        setIsRedeemOpen(false);
        refetch(); // balance, log and redeem history all change
      } else {
        // Kept open so the entered amount is not lost
        setRedeemError(res?.text || "Redeem failed");
      }
    } catch (err) {
      setRedeemError(err?.response?.data?.text || "Redeem failed");
    } finally {
      setSubmitting(false);
    }
  };

  const tabs = [
    { key: "history", label: "Reward History", icon: FiTrendingUp },
    { key: "redeem", label: "Redeem History", icon: FiClock },
    { key: "earn", label: "Available Rewards", icon: FiAward },
  ];

  const renderEmpty = (title, text) => (
    <div className="properties-empty-state">
      <div className="properties-empty-state__icon">
        <FiInbox size={48} />
      </div>
      <h3 className="properties-empty-state__title">{title}</h3>
      {text && <p className="properties-empty-state__text">{text}</p>}
    </div>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <div className="properties-loading-wrap">
          <Spinner loading={true} />
          <p className="mt-3 text-muted">Loading rewards...</p>
        </div>
      );
    }

    if (activeTab === "earn") {
      if (actions.length === 0) return renderEmpty("No rewards available right now");
      return (
        <div className="properties-grid">
          {actions.map((action) => (
            <div key={action.id || action.actionType} className="rewards-card">
              <span className="badge bg-primary-subtle text-primary fw-semibold align-self-start">
                +{action.points} Points
              </span>
              <h5 className="fw-bold text-dark mb-0">{formatRewardTitle(action.actionType)}</h5>
            </div>
          ))}
        </div>
      );
    }

    if (activeTab === "history") {
      if (visibleLogs.length === 0) {
        if (hasFilters) return renderEmpty("No matching rewards", "Try adjusting the date range.");
        const hint = actions.length
          ? `Earn points by: ${actions.map((a) => formatRewardTitle(a.actionType)).join(", ")}.`
          : "Complete rewarded activities in the app to start earning.";
        return renderEmpty("No rewards yet", hint);
      }
      return (
        <div className="table-responsive bg-white rounded-3 border">
          <table className="table align-middle mb-0">
            <thead>
              <tr>
                <th>Action</th>
                <th>Points</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {visibleLogs.map((log) => (
                <tr key={log.id}>
                  <td className="fw-semibold">{formatRewardTitle(log.actionType) || "—"}</td>
                  <td>
                    <span className={`badge ${log.points > 0 ? "bg-success" : "bg-danger"}`}>
                      {log.points > 0 ? `+${log.points}` : log.points} Points
                    </span>
                  </td>
                  <td className="text-secondary">{formatDate(log.created_on, true)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (visibleRedemptions.length === 0) {
      return hasFilters
        ? renderEmpty("No matching redeem requests", "Try adjusting the status or date range.")
        : renderEmpty("No redeem requests", "Redeem your points and your requests will appear here.");
    }
    return (
      <div className="table-responsive bg-white rounded-3 border">
        <table className="table align-middle mb-0">
          <thead>
            <tr>
              <th>Points</th>
              <th>Amount</th>
              <th>Requested</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {visibleRedemptions.map((req) => {
              const badge = STATUS_BADGE[normaliseStatus(req.status)];
              return (
                <tr key={req.id}>
                  <td className="fw-semibold">{req.redeem_points} Points</td>
                  <td>₹{Number(req.redeem_amount || 0).toLocaleString("en-IN")}</td>
                  <td className="text-secondary">{formatDate(req.created_on)}</td>
                  <td>
                    <span className={`badge ${badge.className}`}>{badge.label}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="properties-module-page">
      <Notification />

      {/* Page Header */}
      <div className="property-page-header">
        <div className="property-page-header__content">
          <div className="property-page-header__badge">
            <FiGift className="property-page-header__badge-icon" />
            <span>Rewards Hub</span>
            {!loading && !error && (
              <span className="property-page-header__count-tag">{points.toLocaleString("en-IN")} Points</span>
            )}
          </div>
          <h1 className="property-page-header__title">Rewards</h1>
          <p className="property-page-header__description">
            Available reward balance{loading || error ? "" : ` · Redeem value ₹${points.toLocaleString("en-IN")}`}
          </p>
        </div>
        <div className="property-page-header__actions">
          <button
            type="button"
            className="property-page-header__create-btn"
            onClick={openRedeem}
            disabled={loading || Boolean(error) || points < MIN_POINTS_TO_REDEEM}
            title={points < MIN_POINTS_TO_REDEEM ? `You need at least ${MIN_POINTS_TO_REDEEM} points to redeem` : undefined}
          >
            <FiGift size={18} />
            <span>Redeem Rewards</span>
          </button>
        </div>
      </div>

      {/* Stats (same three figures as mobile) */}
      {!loading && !error && (
        <div className="rewards-stats">
          <div className="rewards-stat">
            <div className="rewards-stat__value">{points.toLocaleString("en-IN")}</div>
            <div className="rewards-stat__label">Available</div>
          </div>
          <div className="rewards-stat">
            <div className="rewards-stat__value">{totals.earned.toLocaleString("en-IN")}</div>
            <div className="rewards-stat__label">Earned</div>
          </div>
          <div className="rewards-stat">
            <div className="rewards-stat__value">{totals.redeemed.toLocaleString("en-IN")}</div>
            <div className="rewards-stat__label">Redeemed</div>
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-danger d-flex align-items-center justify-content-between p-3 rounded-3 my-3" role="alert">
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={refetch}>
            Retry
          </button>
        </div>
      )}

      {/* Tabs + filters */}
      <div className="property-filter-toolbar">
        <div className="property-filter-toolbar__tabs" style={activeTab === "earn" ? { marginBottom: 0, paddingBottom: 0, borderBottom: 0 } : undefined}>
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              className={`property-filter-tab ${activeTab === key ? "property-filter-tab--active" : ""}`}
              onClick={() => setActiveTab(key)}
            >
              <Icon className="property-filter-tab__icon" />
              <span>{label}</span>
            </button>
          ))}
        </div>

        {activeTab !== "earn" && (
          <div className="property-filter-toolbar__controls">
            <div className="property-filter-toolbar__selects">
              {activeTab === "redeem" && (
                <div className="property-filter-select-wrap">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="property-filter-select"
                    aria-label="Filter by status"
                  >
                    <option value="">All Statuses</option>
                    {REDEEM_STATUS_OPTIONS.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <label className="rewards-date">
                <span>From</span>
                <input type="date" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} />
              </label>
              <label className="rewards-date">
                <span>To</span>
                <input type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} />
              </label>
              {(activeTab === "redeem" ? hasFilters : Boolean(dateFrom || dateTo)) && (
                <button type="button" className="property-filter-reset-btn" onClick={resetFilters} title="Reset Filters">
                  <FiRotateCcw size={14} />
                  <span>Reset</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {renderContent()}

      {/* Redeem */}
      <Modal show={isRedeemOpen} onHide={() => !submitting && setIsRedeemOpen(false)} centered>
        <form onSubmit={handleRedeem}>
          <Modal.Header closeButton={!submitting}>
            <Modal.Title style={{ fontSize: "18px", fontWeight: 700 }}>Redeem Rewards</Modal.Title>
          </Modal.Header>
          <Modal.Body className="d-flex flex-column gap-3">
            <div className="text-center p-3 bg-light rounded-3">
              <div className="text-muted small">Available Reward Balance</div>
              <div className="fs-3 fw-bold">{points.toLocaleString("en-IN")} Points</div>
              <div className="text-muted small">Redeem Value ₹{points.toLocaleString("en-IN")}</div>
            </div>
            <div className="d-flex flex-wrap gap-2">
              {QUICK_REDEEM_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  className={`btn btn-sm ${Number(redeemAmount) === option ? "btn-primary" : "btn-outline-primary"}`}
                  disabled={option > points || submitting}
                  onClick={() => setRedeemAmount(String(option))}
                >
                  {option}
                </button>
              ))}
            </div>
            <div>
              <label htmlFor="redeem-points" className="form-label fw-semibold">
                Points to redeem
              </label>
              <input
                id="redeem-points"
                type="number"
                min="1"
                max={points}
                className="form-control"
                placeholder="Enter points"
                value={redeemAmount}
                onChange={(e) => setRedeemAmount(e.target.value)}
                disabled={submitting}
                aria-invalid={redeemError ? "true" : undefined}
              />
            </div>
            {redeemError && (
              <div className="alert alert-danger py-2 small mb-0" role="alert">
                {redeemError}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-outline-secondary" onClick={() => setIsRedeemOpen(false)} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting || !redeemAmount}>
              {submitting ? "Submitting..." : "Submit Request"}
            </button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
};

export default CustomerRewardsHub;
